import { IADOCEANadUnit, IADOCEANService, IAOServiceSlot } from './service-adocean.types';
import { IAd, IPluginInfo, IServices, ISlot, KeywordsArray, KeywordsMap } from '../../../uanc.types';
import { addSlotEvent, buildServiceSlotsConfig, getSlotsByAdServer } from '../ad-services.utils';
import { AD_SERVERS, AD_STATUS, RODO_PERMISSION } from '../../../configs/constants';
import { getNotLazyLoadingSlots, verifySlotsToDefined } from '../embedding.utils';
import { isArrayType, isDefined, isEmpty, isStringType } from '../../../helpers/js-utils';
import DebugUANC from '../../../core/version/debug';
import { iwaAddEvent } from '../../iwa-add-event';
import { PLUGIN_NAME } from '../../plugins/config';

/**
 *  Przygotowuje domyślną/startową konfigurację ADOCEANServices. Uzupełnia konfiguracje slotów do osadzenia.
 *
 * @returns {IADOCEANService} konfiguracja ADOCEANServices
 */
export const prepareInitialADOCEANServiceConfig = (): IADOCEANService => {
    const { config, services } = window.Inpl.Ad;

    let configADOCEANService: IADOCEANService = {
        onEmit: false,
        consent: false,
        isEnabled: true,
        level: 3,
        adUnits: {},
        type: 'ADOCEAN',
        keywords: config!.keywords.ADOCEAN ? [...config!.keywords.ADOCEAN.split(',')] : [],
        slots: [],
        linkSponsPendingItems: {
            data: {},
            dataRequests: {},
        },
        forceFIF: window.forceFIF,
        master: config!.master,
        emptyData: {
            hbTmpFunctionName: '',
            hbParams: [],
            hbType: '',
        },
        groupExecutionItem: {},
    };

    if (services?.ADOCEANService) {
        configADOCEANService = {
            ...services.ADOCEANService,
            ...configADOCEANService,
        };
    }

    const slotsADOCEAN: ISlot[] = getSlotsByAdServer(AD_SERVERS.ADOCEAN, config!.slots);
    configADOCEANService.slots = buildServiceSlotsConfig(AD_SERVERS.ADOCEAN, slotsADOCEAN) as IAOServiceSlot[];

    return configADOCEANService;
};

/**
 * Dodaje podstawową konfigurację ado
 *
 * @returns {void}
 */
export const setADOConfig = (): void => {
    const { plugins, services } = window.Inpl.Ad as IAd;
    const { keywords } = plugins.RodoKeyword!;
    const { forceFIF } = services.ADOCEANService!;

    const consent = RODO_PERMISSION.indexOf(`rodo_${(keywords as string[])[0]}`) !== -1;

    const adoConfig = {
        mode: 'new',
        consent,
        fif: {
            enabled: true,
            force: forceFIF,
            sequential: true,
        },
        protocol: 'https:',
        xml: false,
        characterEncoding: true,
    };

    window.ado.config(adoConfig);
};

/**
 * Metoda ustawia wartości targetowania reklam publikowanych na stronie pod względem keywords konkretnych pluginów.
 *
 * @param {PLUGIN_NAME} pluginName - nazwa pluginu
 * @param {string} key - klucz targetowania, nazwa parametru kierowania reklam
 * @returns {KeywordsArray}
 */
const setTargetingPluginKeywords = (pluginName: Exclude<PLUGIN_NAME, PLUGIN_NAME.TCF_KEYWORDS>, key?: string): KeywordsArray => {
    const { plugins, UANC } = window.Inpl.Ad;

    if (plugins?.[pluginName]) {
        const { isEnabled, keywords } = plugins?.[pluginName] as IPluginInfo;

        if (isEnabled && keywords) {
            if (key) {
                return (keywords as KeywordsArray).map((item: string) => key + item) as KeywordsArray;
            }
            return keywords as KeywordsArray;
        }
    } else {
        UANC instanceof DebugUANC && UANC.warn(`ADOCEAN: Plugin "${pluginName}" is not enabled`);
    }
    return [];
};

/**
 * Metoda przygotowuje tablice ze słowami kluczowymi do przekazania do adserwera.
 *
 * @returns {KeywordsArray}
 */
const getKeywords = (): KeywordsArray => {
    const {
        UANC,
        services,
        detection: { adKeywords, pageURI },
    } = window.Inpl.Ad as IAd;
    UANC instanceof DebugUANC && UANC.info('ADOCEAN: getKeywords ', '');

    const customKeywords = services?.ADOCEANService?.keywords || [];

    let keys = pageURI.split(',');
    keys = keys.concat(customKeywords);

    if (adKeywords && !isEmpty(adKeywords)) {
        keys.push(adKeywords);
    }

    keys = keys.concat(setTargetingPluginKeywords(PLUGIN_NAME.RODO_KEYWORD, 'rodo_'));
    keys = keys.concat(setTargetingPluginKeywords(PLUGIN_NAME.CUSTOM_KEYWORDS));
    keys = keys.concat(setTargetingPluginKeywords(PLUGIN_NAME.NETSPRINT));

    return keys;
};

/**
 * Inicjuje proces osadzania reklam na podstawie konfiguracji pobranych z ADC.
 * Filtruje sloty które są możliwe do osadzenia, definiuje obiekt ado.master, uruchamia request do adserwera
 *
 * @returns {void}
 */
export const embeddingSlots = () => {
    const { ADOCEANService } = window.Inpl.Ad.services as IServices;

    if (!ADOCEANService?.slots.length) return;

    const notLazyLoadingSlots: IAOServiceSlot[] = getNotLazyLoadingSlotsOnSite(ADOCEANService.slots);
    const slotsToDefined: IAOServiceSlot[] = verifySlotsToDefined(notLazyLoadingSlots);

    setADOMaster(slotsToDefined);
};

/**
 * Dodaje konfigurację do ado.master, który wywołuję request do serwera reklam ADOCEAN
 *
 * @param {IAOServiceSlot} slots - lista slotów ADOCEAN do pobrania z serwera reklam
 * @returns {void}
 */
export const setADOMaster = (slots: IAOServiceSlot[]): void => {
    const {
        config: { master, keywords },
        testAB,
    } = window.Inpl.Ad as IAd;

    const masterConfig = {
        id: master,
        server: 'hub.com.pl',
        contentType: 'js',
        keys: getKeywords(),
        slaveIds: slots.map((slot) => slot.slotid),
    };

    iwaAddEvent(
        'trackEvent',
        'uanc',
        'slots',
        'request',
        `${slots.map((slot) => slot.name).join(',')}`,
        '',
        testAB?.currentTest ?? '',
        testAB?.currentTest ? JSON.stringify(keywords) : ''
    );

    window.ado.master(masterConfig);
};

/**
 * Filtruje podane sloty, bez włączonego LL i mają odpowiadający element w DOM.
 *
 * @param {IAOServiceSlot[]} slots - Tablica slotów reklamowych do przefiltrowania.
 * @returns {IAOServiceSlot[]} - Tablica slotów, bez włączonego LL i mają pasujący element DOM z identyfikatorem w formacie ad-{slot.name}.
 */
export const getNotLazyLoadingSlotsOnSite = (slots: IAOServiceSlot[]): IAOServiceSlot[] => {
    return getNotLazyLoadingSlots(slots).filter((slot) => document.getElementById(`ad-${slot.name}`));
};

/**
 * Wybiera sloty które maja więcej niż 1 kontener do osadzenia, ( sloty replikowane do róznych kontenerów )
 *  - wybiera sloty gdzie jest wiecej niż 1 placementIds
 *  - usuwa pierwszy element z placementIds, w którym osadzi reklamę
 *  - przekazuję slot do adoMaster w celu wywołania requestu po reklamę
 *
 *  Metoda musi być wywołana po zakończeniu eventu onEmit z ado, w przeciwnym razie renderowanie zostanie przerwane i
 *  reklamy nie zostaną wyświetlone.
 *
 * @returns {void}
 */
// const getReplicateSlotInContainers = () => {
//     const { ADOCEANService } = window.Inpl.Ad.services as IServices;
//
//     const slotsReplicated = ADOCEANService!.slots.reduce<IAOServiceSlot[]>(
//         (acc, slot) => (slot.placementIds.length > 1 ? (slot.placementIds.shift(), acc.push(slot)) : null, acc),
//         []
//     );
//
//     slotsReplicated.length > 0 && setADOMaster(slotsReplicated);
// };

/**
 * Definijuje metodę nasłuchującą ado.onEmit wywoływaną w momencie odpowiedzi z adserwera.
 * Biblioteka ado przekazuje w wywołaniu tej funkcji id mastera oraz kody reklam
 *  - {string} mId - kod master
 *  - {string} mId_0 - kod master z _0? - do wyjaśnienia do czego to można użyć
 *  - {[key: string]: IADOCEANadUnit} ADOCodes - lista kodów reklam
 *  - ADOCEANService!.onEmit = true; - potrzebujemy tylko jednego callbacka eventu onEmin więc gdy tylko zostanie dodany
 *      nie dodajemy następnych
 *
 * @param { () => void } fn - callback do wywołania po pierwszym wystąpieniu onEmit np ustawienie service ADOCEAN na Ready
 * @returns {void}
 */
export const setOnEmit = (fn?: () => void): void => {
    const { ADOCEANService } = window.Inpl.Ad.services as IServices;

    !ADOCEANService!.onEmit &&
        window.ado.onEmit((mId: string, mId_0: string, ADOCodes: { [key: string]: IADOCEANadUnit }) => {
            //todo iwa/event etc
            saveAdCodes(ADOCodes);
            fn && fn();
            fn && (fn = () => {});
            // getReplicateSlotInContainers();
        });
    ADOCEANService!.onEmit = true;
};

/**
 * Definijuje metodę która zbiera statystyki dla servera ADOCEAN
 *
 * @returns {void}
 */
export const setOnMaster = (): void => {
    const { master } = window.Inpl.Ad.services!.ADOCEANService!;
    window.ado.onMaster(master, () => {
        hbol();
    });
};

/**
 * Zapisuje kody zwrócone przez serwer ADOCEAN w serwisie ADOCEANService w zmiennej adUnits
 * dodatkowo:
 *  - podmienia parametr ltd w zwróconym kodzie pobrany z pluginie TCFKeywords
 *  - uruchamia definiowanie adUnit
 *
 * @param {{[key: string]: IADOCEANadUnit}} ADOCodes - Tablica slotów reklamowych do przefiltrowania.
 * @returns {void}
 */
const saveAdCodes = (ADOCodes: { [key: string]: IADOCEANadUnit }): void => {
    const {
        services: { ADOCEANService },
        plugins,
        UANC,
    } = window.Inpl.Ad as IAd;

    UANC instanceof DebugUANC && UANC.table('ADOCEAN: ADOCodes from server', ADOCodes);

    Object.entries(ADOCodes).forEach(([slotId, adCode]: [slotId: string, adCode: IADOCEANadUnit]) => {
        if (isDefined(adCode)) {
            const { ltd } = plugins.TCFKeywords?.keywords as KeywordsMap;
            if (adCode.code) {
                if (!adCode.isEmpty) {
                    adCode.code = adCode.code.replace(
                        /(<img\b[^>]*\bsrc=\\"[^"]*?);ltd=<%%PATTERN:ltd%%>([^"]*")/i,
                        '$1;ltd=' + ltd + '$2'
                    );
                    adCode.code = adCode.code.replace(/(<img\b[^>]*\bsrc=\\"[^"]*?);ltd=;([^"]*")/i, '$1;ltd=' + ltd + ';$2');
                    //todo tymczasowo do testów powielania slotów
                    adCode.code = adCode.code.replaceAll(/inpl_ad_170056657543/g, 'inpl_ad_170056657543' + new Date().getTime());
                }
                ADOCEANService!.adUnits[slotId] = {
                    ...adCode,
                    isOnSite: false,
                };
                defineAdUnit(slotId);
            }
        }
    });

    UANC instanceof DebugUANC && UANC.table('ADOCEAN: Saved ADOCOdes', window.Inpl.Ad.services?.ADOCEANService?.adUnits);
};

/**
 * Funkcja odpowiedzialna za tworzenie i konfigurację reklamy. Tworzy kontener reklamowy
 * z id specyficznym dla ADOCEAN w odpowiednim placementId.
 *
 * embedAd: wywołuje metodę ado osadzającą reklame
 *
 * @param {string} slotId - id kontenera DOM, w którym załadowana ma być reklama
 * @returns { Promise<string> } zwraca nazwę zdefiniowanego placementu
 */
const defineAdUnit = (slotId: string): void => {
    const { UANC } = window.Inpl.Ad as IAd;
    const { slots, adUnits } = window.Inpl.Ad.services!.ADOCEANService!;
    const slot = slots.find((item) => item.slotid === slotId);
    if (!slot) return;

    const { placementIds, slotid, name } = slot;
    adUnits[slotId].name = name;

    //Wyślij event empty jeśli pusta zwrotka
    if (adUnits[slotId].isEmpty) addSlotEvent(slot, placementIds[0], AD_STATUS.EMPTY);

    const adContainer = document.getElementById(placementIds[0]);
    if (!adContainer) return;

    adContainer.innerHTML = '';

    const adDiv = document.createElement('div');
    adDiv.id = slotid;
    adDiv.setAttribute('align', 'center');
    adDiv.style.display = 'none';
    adContainer.appendChild(adDiv);

    UANC instanceof DebugUANC && UANC.info('ADOCEAN: EmbedAd', name, slotid, `isEMPTY: ${!!adUnits[slotId].isEmpty}`);

    window.ado.onAd(slotid, (isAd: boolean) => {
        //todo do wywalenia jak pozbedziemy sie sportu i wydarzen
        // isAd && ((document.querySelector(`[id^=react_component_] .ad#${slot.placementIds[0]}`) as HTMLElement).style.display = 'block');
        isAd &&
            (slot['renderedPlacements'] = {
                ...(slot['renderedPlacements'] ?? {}),
                [placementIds[0]]: {
                    isDisplayed: true, // set in makePlacementDisplayed
                    currentSize: [0, 0],
                },
            });

        if (isAd && (name.startsWith('artspons') || name.startsWith('m_artspons'))) {
            addSlotEvent({ ...slot, dfp: false }, null, AD_STATUS.ART_SPONS);
        }

        isAd && addSlotEvent(slot, placementIds[0], AD_STATUS.LOADED);
    });
    embedAd(name, slotid);
};

/**
 * Funkcja odpowiedzialna za tworzenie i konfigurację reklamy. Tworzy kontener reklamowy
 * z id specyficznym dla ADOCEAN w odpowiednim placementId.
 *
 * embedAd: wywołuje metodę ado osadzającą reklame
 *
 * @param {string} name - nazwa miejsca reklamowego
 * @param {string} slotid - id slotu ADOCEAN/id elementu DOM
 * @returns { Promise<string> } zwraca nazwę zdefiniowanego placementu
 */
const embedAd = (name: string, slotid: string): void => {
    if (!isDefined(slotid) && !isStringType(slotid)) {
        return;
    }

    if (!isDefined(name) && !isStringType(name)) {
        return;
    }

    window.ado.slave(slotid, {
        myMaster: window.Inpl.Ad.services?.ADOCEANService?.master,
    });
};
/**
 * Zwraca informację nt pustych slotów
 *
 * @returns { {[key:string]:number} } - lista miejsc
 */
const hbol = (): { [key: string]: number } => {
    const { hbType, hbParams } = window.Inpl.Ad.services!.ADOCEANService!.emptyData || {};
    const { adUnits } = window.Inpl.Ad.services!.ADOCEANService! || {};

    if (isStringType(hbType) && isArrayType(hbParams)) {
        const places: { [key: string]: number } = {};
        const hbImgs: HTMLImageElement[] = [];

        const src = `https://${hbType}hit.gemius.pl/_${new Date().getTime()}/redot.gif`;
        let cnt = 0,
            icnt = 0,
            ids = '';

        for (let param = 0; param < hbParams.length; param++) {
            ids += (ids ? '&' : '?') + hbParams[param].args;

            if (++cnt % 5 === 0) {
                hbImgs[++icnt] = new Image();
                hbImgs[icnt].src = src + ids;
                ids = '';
            }
        }

        if (!isEmpty(ids)) {
            // } !== '') {
            hbImgs[++icnt] = new Image();
            hbImgs[icnt].src = src + ids;
        }

        Object.values(adUnits).forEach((adCode) => {
            if (isDefined(adCode.isOnSite) && isDefined(adCode.name)) {
                places[adCode.name] = adCode.isOnSite ? 1 : 0;
            }
        });

        return places;
    }

    return {};
};
